import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING } from "const"
import { CcLibraryItem } from "server/models"
import { StateType } from "client/utils"
import {
    StoreType,
    DeleteManyCcLibraryItems,
    UpdateManyCcLibraryItems,
    setCcLibraryItemConfirmDelete,
    setCcLibraryItemReloadList,
} from "client/store"
import {
    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
    CheckButton,
    Button,
    SpinnerIcon,
    TableNestedIds,
    TableGroupedIds,
} from "client/components"

const ConfirmDelete = () => {
    const itemToDelete = useSelector((state: StoreType) => state.ccLibraryItem.confirmDelete)
    const nested = useSelector((state: StoreType) => state.ccLibraryItem.itemGroupedIds)
    const itemList = useSelector((state: StoreType) => state.ccLibraryItem.itemList)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [deleteChildren, setDeleteChildren] = useState(false)
    const [deleteState, setDeleteState] = useState<StateType>(IDLE)

    const onCancel = () => {
        dispatch(setCcLibraryItemConfirmDelete(null))
        setDeleteChildren(false)
    }

    const onConfirm = () => {
        setDeleteState(PENDING)

        const deleteItems: number[] = [itemToDelete.id]
        const updateItems: CcLibraryItem[] = []

        // find nested item
        let nestedItem: TableNestedIds = null
        let nestedItemParent: TableNestedIds = null
        let nestedHeadingParent: TableGroupedIds = null

        const searchItem = (nestedIds: readonly TableNestedIds[]) => {
            nestedIds.every((row) => {
                if (row.id === itemToDelete.id) {
                    nestedItem = row
                    return false // break
                }
                if (row.id === itemToDelete.itemId) nestedItemParent = row
                searchItem(row.nestedIds)
                return true // continue
            })
        }
        const searchHeading = (nestedHeadings: readonly TableGroupedIds[]) => {
            nestedHeadings.every((row) => {
                searchItem(row.nestedIds)
                if (row.groupId === itemToDelete.headingId) nestedHeadingParent = row
                if (!row.groupId && !itemToDelete.headingId) nestedHeadingParent = row // root
                if (nestedItem) {
                    return false // break
                }
                searchHeading(row.nestedGroupedIds)
                return true // continue
            })
        }
        searchHeading([nested])


        // delete nested children
        if (deleteChildren) {
            const recursiveDeleteItems = (nestedItems: readonly TableNestedIds[]) => {
                nestedItems.forEach((row) => {
                    deleteItems.push(Number(row.id))
                    recursiveDeleteItems(row.nestedIds)
                })
            }
            recursiveDeleteItems(nestedItem.nestedIds)

        // move nested children
        } else {
            // define new item order
            const newParentArray = nestedItemParent
                ? nestedItemParent.nestedIds
                : nestedHeadingParent.nestedIds
            const itemLength = newParentArray.length
            const lastNestedItem = itemLength && newParentArray[itemLength - 1]
            const lastItem = lastNestedItem ? itemList[Number(lastNestedItem.id)] : null
            const itemOrder = lastItem ? lastItem.order + 1 : 0

            // move child items to upper level
            nestedItem.nestedIds.forEach((row) => {
                const itemObject: CcLibraryItem = {
                    ...itemList[Number(row.id)],
                    headingId: itemToDelete.headingId,
                    itemId: itemToDelete.itemId,
                    order: itemOrder + updateItems.length,
                }
                updateItems.push(itemObject)
            })
        }

        Promise.all([
            DeleteManyCcLibraryItems(deleteItems),
            UpdateManyCcLibraryItems(updateItems),
        ]).then(() => {
            setDeleteState(IDLE)
            setDeleteChildren(false)
            dispatch(setCcLibraryItemConfirmDelete(null))
            dispatch(setCcLibraryItemReloadList(false))
            setTimeout(() => {
                dispatch(setCcLibraryItemReloadList(true))
            }, 0)
        })
    }

    return (
        <ConfirmWrapper
            show={itemToDelete !== null}
            onClose={onCancel}
        >
            <Confirm align="start">
                <ConfirmTitle>{itemToDelete && `${itemToDelete.publicId} ${itemToDelete.compliantItem}`}</ConfirmTitle>
                <ConfirmDescription>{lang.deleteItemConfirm}</ConfirmDescription>

                <CheckButton
                    checked={deleteChildren}
                    onChange={() => setDeleteChildren(!deleteChildren)}
                    title={lang.deleteChildItems}
                />

                <ConfirmBottom>
                    <Button row onClick={onCancel}>{lang.cancel}</Button>
                    <Button variant="success" row onClick={onConfirm}>
                        {deleteState === PENDING ? <SpinnerIcon /> : lang.confirm}
                    </Button>
                </ConfirmBottom>
            </Confirm>
        </ConfirmWrapper>
    )
}
export default ConfirmDelete
